import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "./RightDrawer.css";

// Simple icons for the menu
const Icons = {
  Chevron: ({ expanded }) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"></polyline></svg>
};

/**
 * RightDrawer - navigation drawer with sections and subpages.
 * props:
 * - open: boolean
 * - onClose: function
 * - isAuthenticated: boolean
 */
export default function RightDrawer({ open, onClose, isAuthenticated }) {
  const navigate = useNavigate();
  const { current, simulateGuestMode } = useAuth();
  const isDemoTester = current?.role === 'demo_tester';

  const [expanded, setExpanded] = useState({
    studium: true,
    diagnostika: true,
    platforma: false,
    ucet: true,
    admin: false
  });

  const go = (path) => {
    navigate(path);
    onClose();
  };

  const toggle = (section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // close on Escape key for better UX
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* backdrop: use 'open' class when opened */}
      <div className={`drawer-backdrop ${open ? 'open' : ''}`} onClick={onClose} aria-hidden={!open}></div>

      <aside className={`right-drawer ${open ? "open" : ""}`} role="dialog" aria-hidden={!open}>
        <div style={{display:'flex', justifyContent:'flex-end'}}>
          <button className="drawer-close" onClick={onClose} aria-label="Zavřít">Zavřít ✕</button>
        </div>

        <div className="drawer-content">
          <nav className="drawer-nav">
            
            {/* Studium Section */}
            <div className="drawer-group">
              <button className="drawer-group-header" onClick={() => toggle('studium')}>
                <span className="group-title">Studium</span>
                <Icons.Chevron expanded={expanded.studium} />
              </button>
              
              {expanded.studium && (
                <div className="drawer-sublist open">
                  <button className="drawer-link" onClick={() => go(isDemoTester ? "/demoinfo" : "/")}>Domovská stránka</button>
                  <button className="drawer-link" onClick={() => go("/lessons/recommended")}>Doporučené lekce</button>
                  {(!current || simulateGuestMode) ? (
                    <>
                      <button className="drawer-link" onClick={() => go("/practice/5")}>Procvičování 5. třídy</button>
                      <button className="drawer-link" onClick={() => go("/practice/9")}>Procvičování 9. třídy</button>
                    </>
                  ) : (
                    <button className="drawer-link" onClick={() => go("/practice")}>Procvičování</button>
                  )}
                  <button className="drawer-link" onClick={() => go("/lessons/active")}>Aktivní lekce</button>
                  <button className="drawer-link" onClick={() => go("/dashboard")}>Dashboard</button>
                </div>
              )}
            </div>

            {/* Diagnostika Section */}
            <div className="drawer-group">
              <button className="drawer-group-header" onClick={() => toggle('diagnostika')}>
                <span className="group-title">Diagnostika</span>
                <Icons.Chevron expanded={expanded.diagnostika} />
              </button>
              
              {expanded.diagnostika && (
                <div className="drawer-sublist open">
                  {/* If user is not authenticated show both grades. If authenticated, show only user's grade. */}
                  {(!current || simulateGuestMode) && (
                    <>
                      <button className="drawer-link" onClick={() => go("/diagnostic?grade=5")}>Diagnostika 5. třídy</button>
                      <button className="drawer-link" onClick={() => go("/diagnostic?grade=9")}>Diagnostika 9. třídy</button>
                    </>
                  )}

                  {current && !simulateGuestMode && (
                    (() => {
                      const gradeKey = (current.grade === 'grade_9' || current.grade === '9') ? '9' : '5';
                      return (
                        <button className="drawer-link" onClick={() => go(`/diagnostic?grade=${gradeKey}`)}>Diagnostika {gradeKey}. třídy</button>
                      );
                    })()
                  )}
                </div>
              )}
            </div>

            {/* O platformě Section */}
            <div className="drawer-group">
              <button className="drawer-group-header" onClick={() => toggle('platforma')}>
                <span className="group-title">O platformě</span>
                <Icons.Chevron expanded={expanded.platforma} />
              </button>
              
              {expanded.platforma && (
                <div className="drawer-sublist open">
                  <button className="drawer-link" onClick={() => go("/o-platforme")}>O MatikaMistr</button>
                  <button className="drawer-link" onClick={() => go("/jak-to-funguje")}>Jak to funguje</button>
                </div>
              )}
            </div>

            {/* Účet a podpora Section */}
            <div className="drawer-group">
              <button className="drawer-group-header" onClick={() => toggle('ucet')}>
                <span className="group-title">Účet a podpora</span>
                <Icons.Chevron expanded={expanded.ucet} />
              </button>
              
              {expanded.ucet && (
                <div className="drawer-sublist open">
                  {isAuthenticated ? (
                    <>
                      <button className="drawer-link" onClick={() => go("/profile")}>Můj profil</button>
                      <button className="drawer-link" onClick={() => go("/live-support")}>Live podpora</button>
                      <button className="drawer-link" onClick={() => go("/premium")}>Premium</button>
                      <button className="drawer-link" onClick={() => go("/settings")}>Nastavení</button>
                    </>
                  ) : (
                    <>
                      <button className="drawer-link" onClick={() => go("/login")}>Přihlásit se</button>
                      <button className="drawer-link" onClick={() => go("/register")}>Registrace</button>
                      <button className="drawer-link" onClick={() => go("/live-support")}>Live podpora</button>
                    </>
                  )}
                </div>
              )}
            </div>

            {current?.role === 'admin' && (
              <div className="drawer-group">
                <button className="drawer-group-header" onClick={() => toggle('admin')}>
                  <span className="group-title">Admin sekce</span>
                  <Icons.Chevron expanded={expanded.admin} />
                </button>
                
                {expanded.admin && (
                  <div className="drawer-sublist open">
                    <button className="drawer-link" onClick={() => go('/admin/tasks')}>Správa úloh</button>
                    <button className="drawer-link" onClick={() => go('/admin/practice')}>Správa procvičování</button>
                    <button className="drawer-link" onClick={() => go('/admin/goals')}>Admin cíle</button>
                    <button className="drawer-link" onClick={() => go('/admin/gamification')}>Gamifikace</button>
                    <button className="drawer-link" onClick={() => go('/admin/correspondence')}>Korespondence</button>
                      <button className="drawer-link" onClick={() => go('/admin/schools')}>Školy</button>
                  </div>
                )}
              </div>
            )}

          </nav>

          {/* Footer note removed per request: no extra navigation hint for users */}
        </div>
      </aside>
    </>
  );
}